import { useCallback } from "react";
import { getHost } from "./getHost";

// Imperative tracking — wraps the host's `trackGA4AndMixpanel`. Use
// when you want to fire a track call inside a handler:
//
//   const track = useTrack();
//   ...
//   const onClick = () => {
//     track("Team Management > Open Members tab", {
//       eventCategory: "MY_TEAM",
//     });
//     setTab("members");
//   };
//
// For declarative auto-tracking on a child's click — closer to the
// Vue `v-aha-emit-action` directive — use the <TrackOnClick> wrapper.
//
// When the host bridge is missing (standalone dev), this is a no-op.

export type TrackFn = (
  eventName: string,
  payload?: Record<string, unknown>,
) => void;

export function useTrack(): TrackFn {
  return useCallback((eventName, payload) => {
    const host = getHost();
    if (!host?.trackGA4AndMixpanel) return;
    try {
      // The presenter's trackGA4AndMixpanel (presenter src/util.js) derives the
      // *Mixpanel* event name from `payload.eventAction` — GA4 uses `eventName`,
      // Mixpanel uses `eventAction`. A call that omits eventAction reaches
      // Mixpanel as `track(undefined, …)` and is lost. That silently dropped
      // every team-mgmt event without an explicit eventAction once the legacy
      // presenter (which always set it) was retired at the Jun 2026 cutover —
      // invite funnel, remove/leave, withdraw and tab-nav flatlined while
      // eventAction-bearing events (license/role/upgrade) kept landing.
      //
      // Default eventAction to eventName so every call is tracked under its
      // name. Spreading payload last means call sites that set their own
      // eventAction (e.g. "Select license" → "…Update member's license") still
      // win — no change to events that already worked.
      host.trackGA4AndMixpanel(eventName, { eventAction: eventName, ...payload });
    } catch {
      // Never let analytics failures bubble into UI handlers.
    }
  }, []);
}
